/**
 * Track-level animation path builder (Plan 26 Phase 1d, Option A+).
 *
 * Pure lon/lat math — no canvas projection, no React. Callers project the
 * returned {lon, lat} through `projectToCanvas`/`interpolateTrackPosition`.
 *
 * Model: a TRAVEL micro-step crosses one junction — the tail of `fromTrack`
 * ending at the switch, then `toTrack` away from the switch. Distance is
 * carried in feet per segment (`properties.lengthFeet`); positions are
 * ratio-mapped within each oriented polyline exactly like
 * `interpolateTrackPosition`, so feet stay authoritative even where
 * coordinate degrees and feet diverge.
 */

/** Minimal track feature shape (GeoJSON LineString + lengthFeet). */
export interface TrackFeatureLike {
  id: string
  geometry: { type: string; coordinates: number[][] }
  properties?: { lengthFeet?: number; name?: string }
}

/** Minimal switch feature shape (GeoJSON Point anchor at the junction). */
export interface SwitchFeatureLike {
  id: string
  geometry: { type: string; coordinates: number[] }
  properties?: {
    name?: string
    facingTrackId?: string
    normalTrackId?: string
    reverseTrackId?: string
  }
}

export type LonLat = [number, number]

/** One oriented polyline contribution to a leg, in direction of travel. */
export interface PathSegment {
  trackId: string
  /** Coordinates oriented along travel direction (junction end last on
   *  the "from" side, first on the "to" side). May be a trimmed sub-range. */
  coords: LonLat[]
  /** Authoritative feet covered by `coords` — the trimmed range in feet,
   *  not the whole track length. */
  spanFeet: number
  /** Authoritative full-track feet (for downstream length math). */
  trackLengthFeet: number
  /** Feet offset (from leg start) where this segment begins. */
  startFeet: number
  /** Feet into the ORIENTED polyline where `coords` begins (trim offset).
   *  0 for untrimmed segments. */
  orientedStartFeet: number
  /** True when the polyline was reversed to face travel direction —
   *  A-end→B-end offsets then mirror (`offsetAB = trackLengthFeet - oriented`). */
  flipped: boolean
}

export interface LegPath {
  segments: PathSegment[]
  totalFeet: number
  /** Degrees of gap bridged at the junction (0 on Marietta seed geometry).
   *  >0 means endpoints were snapped — surface this in diagnostics. */
  junctionGapDeg: number
  /** False when the junction was resolved from the nearest endpoint pair
   *  rather than the switch POINT geometry. */
  junctionFromSwitchPoint: boolean
  /** True when either endpoint sat >~30 ft from the junction — geometry
   *  drifted; the player shows a notice rather than silently teleporting. */
  junctionGapExceeded: boolean
}

export interface LegPosition {
  lon: number
  lat: number
  /** True when the segment is traversed B→A — the position's head is
   *  the AB-low end (body extends toward +A→B offsets). */
  flipped?: boolean
  /** Heading in canvas-space degrees (atan2(-dLat, dLon)) — matches
   *  `interpolateTrackPosition`'s convention. */
  angleDeg: number
  /** Track segment the position currently sits on. */
  trackId: string
  /** Position in the segment track's A-end→B-end offset space (feet) —
   *  what `linearOffsetFeet` would read if the entity stopped here. */
  offsetFeet: number
}

export interface BuildLegInput {
  fromTrack?: TrackFeatureLike
  toTrack: TrackFeatureLike
  /** Junction anchor — the switch's POINT coordinates. Null → nearest-
   *  endpoint fallback (flags `junctionFromSwitchPoint: false`). */
  junctionPoint?: LonLat | null
  /** Feet along `fromTrack` (A-end → B-end offset space) where the consist
   *  head enters the leg. Absent → tail covers the whole track into the
   *  junction (loco enters at the far end). Ignored when `fromTrack` is
   *  omitted (loco already at the junction). */
  enterFromOffsetFeet?: number
  /** Where the head stops on `toTrack` (A-end → B-end offset space).
   *  Absent → traverses to the far end. Used for spot-targeted stops. */
  toTrackExitFeet?: number
}

export type BuildLegResult =
  | { ok: true; leg: LegPath }
  | { ok: false; error: string }

/** Degrees ~> feet sanity bound for endpoint snapping (~30 ft at 33.95°N). */
const SNAP_TOLERANCE_DEG = 1e-4

const dist = (a: LonLat, b: LonLat): number =>
  Math.hypot(a[0] - b[0], a[1] - b[1])

const coordsOf = (t: TrackFeatureLike): LonLat[] =>
  t.geometry.coordinates as LonLat[]

const lengthFeetOf = (t: TrackFeatureLike): number =>
  t.properties?.lengthFeet ?? 2000

/** Pick the track endpoint closest to `point` ('first'|'last' index). */
const nearestEnd = (coords: LonLat[], point: LonLat): 'first' | 'last' =>
  dist(coords[0], point) <= dist(coords[coords.length - 1], point)
    ? 'first'
    : 'last'

/**
 * Orient `coords` so that `end`-side sits at `junction` ('last' = junction
 * at tail, 'first' = junction at head). Returns the oriented copy plus the
 * residual gap (degrees) between the chosen endpoint and the junction.
 */
const orientToward = (
  coords: LonLat[],
  junction: LonLat,
  end: 'first' | 'last'
): { oriented: LonLat[]; gapDeg: number } => {
  const at = nearestEnd(coords, junction)
  const oriented = at === end ? coords.slice() : coords.slice().reverse()
  const gapDeg = dist(
    end === 'first' ? oriented[0] : oriented[oriented.length - 1],
    junction
  )
  return { oriented, gapDeg }
}

/** Cumulative degrees per vertex of a polyline. */
const cumulativeDeg = (coords: LonLat[]): number[] => {
  const acc: number[] = [0]
  for (let i = 1; i < coords.length; i++) {
    acc.push(acc[i - 1] + dist(coords[i - 1], coords[i]))
  }
  return acc
}

/**
 * Trim an ORIENTED polyline to the feet range [fromFeet, toFeet] measured
 * in its own direction of travel. `trackLengthFeet` maps feet → polyline
 * fraction. Interpolates new endpoints; clamps to the polyline extent.
 */
export const trimOriented = (
  oriented: LonLat[],
  trackLengthFeet: number,
  fromFeet: number,
  toFeet: number
): LonLat[] => {
  const totalDeg = cumulativeDeg(oriented)
  const extent = totalDeg[totalDeg.length - 1]
  if (extent === 0 || trackLengthFeet <= 0) {
    return oriented.slice()
  }
  const startDeg = Math.max(0, Math.min(fromFeet / trackLengthFeet, 1)) * extent
  const endDeg = Math.max(startDeg, Math.min(toFeet / trackLengthFeet, 1) * extent)

  const pointAtDeg = (d: number): LonLat => {
    let i = 0
    while (i < oriented.length - 1 && totalDeg[i + 1] < d) i++
    const segLen = totalDeg[i + 1] - totalDeg[i]
    const t = segLen > 0 ? (d - totalDeg[i]) / segLen : 0
    const a = oriented[i]
    const b = oriented[Math.min(i + 1, oriented.length - 1)]
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
  }

  const out: LonLat[] = [pointAtDeg(startDeg)]
  for (let i = 1; i < oriented.length - 1; i++) {
    if (totalDeg[i] > startDeg && totalDeg[i] < endDeg) {
      out.push(oriented[i])
    }
  }
  out.push(pointAtDeg(endDeg))
  return out
}

/** Resolve the junction anchor: explicit switch point, else nearest
 *  endpoint pair between the two polylines. */
const resolveJunction = (
  fromCoords: LonLat[] | undefined,
  toCoords: LonLat[],
  junctionPoint?: LonLat | null
): { point: LonLat | null; fromSwitchPoint: boolean; error?: string } => {
  if (junctionPoint) {
    return { point: junctionPoint, fromSwitchPoint: true }
  }
  if (!fromCoords || fromCoords.length === 0) {
    return { point: null, fromSwitchPoint: false }
  }
  // Nearest endpoint pair fallback.
  const fromEnds = [fromCoords[0], fromCoords[fromCoords.length - 1]]
  const toEnds = [toCoords[0], toCoords[toCoords.length - 1]]
  let best: LonLat | null = null
  let bestDist = Infinity
  for (const a of fromEnds) {
    for (const b of toEnds) {
      const d = dist(a, b)
      if (d < bestDist) {
        bestDist = d
        best = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
      }
    }
  }
  return { point: best, fromSwitchPoint: false }
}

/**
 * Build the combined path for one TRAVEL leg: tail of `fromTrack` into the
 * junction + all of `toTrack` away from the junction. When `fromTrack` is
 * omitted the consist is already at the junction (reversal / post-locate).
 */
export const buildTravelLegPath = (input: BuildLegInput): BuildLegResult => {
  const { fromTrack, toTrack } = input
  const toCoords = coordsOf(toTrack)
  if (!toTrack || toCoords.length < 2) {
    return { ok: false, error: `toTrack '${toTrack?.properties?.name ?? toTrack?.id ?? '?'}' has no polyline` }
  }
  const fromCoords = fromTrack ? coordsOf(fromTrack) : undefined
  if (fromTrack && (!fromCoords || fromCoords.length < 2)) {
    return { ok: false, error: `fromTrack '${fromTrack.properties?.name ?? fromTrack.id}' has no polyline` }
  }

  const junction = resolveJunction(fromCoords, toCoords, input.junctionPoint)
  if (!junction.point) {
    return { ok: false, error: 'no junction anchor (missing switch point and fromTrack)' }
  }

  const segments: PathSegment[] = []
  let startFeet = 0
  let junctionGapDeg = 0

  if (fromTrack && fromCoords) {
    const { oriented, gapDeg } = orientToward(fromCoords, junction.point, 'last')
    junctionGapDeg = Math.max(junctionGapDeg, gapDeg)
    const trackFeet = lengthFeetOf(fromTrack)
    // A-end→B-end offset space vs oriented direction: if the polyline was
    // flipped, the entry offset (defined in A→B space) must be mirrored so
    // the trimmed tail stays anchored at the junction end.
    const flipped = oriented[0] !== fromCoords[0]
    // Absent → the consist traverses the whole track into the junction.
    const orientedStartFeet =
      input.enterFromOffsetFeet == null
        ? 0
        : flipped
          ? trackFeet - Math.max(0, Math.min(input.enterFromOffsetFeet, trackFeet))
          : Math.max(0, Math.min(input.enterFromOffsetFeet, trackFeet))
    const trimmed = trimOriented(oriented, trackFeet, Math.min(orientedStartFeet, trackFeet), trackFeet)
    const spanFeet = trackFeet - Math.min(orientedStartFeet, trackFeet)
    if (spanFeet > 0) {
      segments.push({
        trackId: fromTrack.id,
        coords: trimmed,
        spanFeet,
        trackLengthFeet: trackFeet,
        startFeet,
        orientedStartFeet: Math.min(orientedStartFeet, trackFeet),
        flipped,
      })
      startFeet += spanFeet
    }
  }

  {
    const { oriented, gapDeg } = orientToward(toCoords, junction.point, 'first')
    junctionGapDeg = Math.max(junctionGapDeg, gapDeg)
    const trackFeet = lengthFeetOf(toTrack)
    const flipped = oriented[0] !== toCoords[0]
    // Stop offset is expressed in A→B space; in the oriented direction the
    // junction end is 0, so a flipped polyline measures the stop from the
    // far end.
    const spanFeet =
      input.toTrackExitFeet == null
        ? trackFeet
        : flipped
          // Enters at B-end (AB offset = trackFeet); oriented distance
          // from the junction to a stop at AB offset s is trackFeet - s.
          ? trackFeet - Math.max(0, Math.min(input.toTrackExitFeet, trackFeet))
          // Enters at A-end (AB offset 0); distance from junction is s.
          : Math.max(0, Math.min(input.toTrackExitFeet, trackFeet))
    const coords =
      spanFeet < trackFeet
        ? trimOriented(oriented, trackFeet, 0, spanFeet)
        : oriented
    segments.push({
      trackId: toTrack.id,
      coords,
      spanFeet,
      trackLengthFeet: trackFeet,
      startFeet,
      orientedStartFeet: 0,
      flipped,
    })
    startFeet += spanFeet
  }

  return {
    ok: true,
    leg: {
      segments,
      totalFeet: startFeet,
      junctionGapDeg,
      junctionFromSwitchPoint: junction.fromSwitchPoint,
      junctionGapExceeded: junctionGapDeg > SNAP_TOLERANCE_DEG,
    },
  }
}

/** Position at `feet` along a leg (clamped to [0, totalFeet]). */
export const positionAtFeet = (leg: LegPath, feet: number): LegPosition | null => {
  if (leg.segments.length === 0) {
    return null
  }
  const clamped = Math.max(0, Math.min(feet, leg.totalFeet))
  const last = leg.segments[leg.segments.length - 1]
  let seg = last
  for (const s of leg.segments) {
    // Strict <: exactly at the junction is already on the to-track.
    if (clamped < s.startFeet + s.spanFeet || s === last) {
      seg = s
      break
    }
  }
  const local = Math.max(0, Math.min(clamped - seg.startFeet, seg.spanFeet))
  const ratio = seg.spanFeet > 0 ? local / seg.spanFeet : 0

  const cum = cumulativeDeg(seg.coords)
  const extent = cum[cum.length - 1]
  const targetDeg = ratio * extent

  let i = 0
  while (i < seg.coords.length - 1 && cum[i + 1] < targetDeg) i++
  const a = seg.coords[i]
  const b = seg.coords[Math.min(i + 1, seg.coords.length - 1)]
  const segLen = cum[Math.min(i + 1, cum.length - 1)] - cum[i]
  const t = segLen > 0 ? (targetDeg - cum[i]) / segLen : 0
  const lon = a[0] + (b[0] - a[0]) * t
  const lat = a[1] + (b[1] - a[1]) * t
  // Canvas-space heading: y is latitude-inverted downstream.
  const angleDeg = (Math.atan2(-(b[1] - a[1]), b[0] - a[0]) * 180) / Math.PI
  const orientedFeet = seg.orientedStartFeet + local
  const offsetFeet = seg.flipped
    ? seg.trackLengthFeet - orientedFeet
    : orientedFeet
  return { lon, lat, angleDeg, trackId: seg.trackId, offsetFeet, flipped: seg.flipped }
}

/** Arc-length offsets (feet behind the consist head) for an ordered cut:
 *  carLengths[i] + couplingAllowance between cars. Car 0 is loco-adjacent
 *  per the documented A2 assumption (pullEnd is not on the wire). */
export const consistOffsetsFeet = (
  carLengthsFeet: number[],
  couplingAllowanceFeet: number
): number[] => {
  const offsets: number[] = []
  let acc = 0
  for (let i = 0; i < carLengthsFeet.length; i++) {
    acc += carLengthsFeet[i] / 2 // centre of car i
    offsets.push(acc)
    acc += carLengthsFeet[i] / 2 + couplingAllowanceFeet
  }
  return offsets
}

/** Which end of the track polyline faces `junction` ('first' = A-end). */
export const junctionEnd = (track: TrackFeatureLike, junction: LonLat): 'first' | 'last' =>
  nearestEnd(coordsOf(track), junction)

/** Position on a track at an A-end→B-end `offsetFeet` (parked-entity
 *  rendering — no travel orientation). Same ratio-mapping convention as
 *  `interpolateTrackPosition`. */
export const pointOnTrackAB = (
  track: TrackFeatureLike,
  offsetFeet: number
): LegPosition | null => {
  const coords = coordsOf(track)
  if (coords.length === 0) {
    return null
  }
  if (coords.length === 1) {
    return { lon: coords[0][0], lat: coords[0][1], angleDeg: 0, trackId: track.id, offsetFeet }
  }
  const trackFeet = lengthFeetOf(track)
  const cum = cumulativeDeg(coords)
  const extent = cum[cum.length - 1]
  const targetDeg = Math.max(0, Math.min(offsetFeet / Math.max(trackFeet, 1e-6), 1)) * extent

  let i = 0
  while (i < coords.length - 1 && cum[i + 1] < targetDeg) i++
  const a = coords[i]
  const b = coords[Math.min(i + 1, coords.length - 1)]
  const segLen = cum[Math.min(i + 1, cum.length - 1)] - cum[i]
  const t = segLen > 0 ? (targetDeg - cum[i]) / segLen : 0
  return {
    lon: a[0] + (b[0] - a[0]) * t,
    lat: a[1] + (b[1] - a[1]) * t,
    angleDeg: (Math.atan2(-(b[1] - a[1]), b[0] - a[0]) * 180) / Math.PI,
    trackId: track.id,
    offsetFeet,
  }
}

/** Switch-feature convenience: extract the junction POINT for a step's
 *  switchId. Returns null when the feature is missing/not a Point — the
 *  caller's stale-map guard decides whether that disables playback. */
export const junctionPointOf = (sw?: SwitchFeatureLike | null): LonLat | null =>
  sw && sw.geometry?.type === 'Point' && Array.isArray(sw.geometry.coordinates)
    ? (sw.geometry.coordinates as LonLat)
    : null
